import { useState } from "react";
import { Tv, X } from "lucide-react";

const cellKey = (c, r) => `${c}_${r}`;

function Cell({ c, r, tv, selectedTv, onDropTv, onPlace, onRemove, onDragTv }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDropTv(e.dataTransfer.getData("text/tv-id"), c, r); }}
      onClick={() => selectedTv && onPlace(selectedTv, c, r)}
      className={`relative flex items-center justify-center border border-white/60 transition-colors duration-200 ${
        over ? "bg-blue-500/40" : tv ? "bg-slate-900/25" : selectedTv ? "bg-blue-500/10 cursor-pointer hover:bg-blue-500/25" : "bg-slate-900/5"
      }`}
      data-testid={`wall-cell-${cellKey(c, r)}`}
    >
      {tv ? (
        <div
          draggable
          onDragStart={(e) => { e.dataTransfer.setData("text/tv-id", tv.id); onDragTv(tv.id); }}
          className="group flex items-center gap-2 rounded-full bg-white/95 shadow-md px-3 py-1.5 text-xs font-medium text-slate-900 cursor-grab active:cursor-grabbing"
          data-testid={`wall-cell-tv-${tv.id}`}
        >
          <Tv className="h-3.5 w-3.5 text-blue-600" />
          <span className="max-w-[140px] truncate">{tv.name}</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(tv.id); }}
            className="rounded-full p-0.5 text-slate-400 hover:text-red-600 hover:bg-red-50"
            aria-label="Quitar TV de la celda"
            data-testid={`wall-cell-remove-${tv.id}`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <span className="text-[11px] uppercase tracking-widest text-slate-500/80 bg-white/70 rounded px-2 py-0.5">
          {selectedTv ? "Click para poner aquí" : "Arrastra una TV"}
        </span>
      )}
      <span className="absolute left-2 top-1.5 font-mono text-[10px] text-white/90 drop-shadow">{`${r + 1}·${c + 1}`}</span>
    </div>
  );
}

export function WallGrid({ cols, rows, slots, tvsById, preview, selectedTv, onDropTv, onPlace, onRemove, onDragTv }) {
  const byCell = Object.fromEntries(slots.map((s) => [cellKey(s.col, s.row), tvsById[s.tv_id]]));
  const isVideo = preview && (preview.content_type || "").startsWith("video/");
  return (
    <div
      className="relative mx-auto w-full overflow-hidden rounded-xl border-4 border-slate-900 bg-slate-800 shadow-lg"
      style={{ aspectRatio: `${cols * 16} / ${rows * 9}`, maxWidth: `min(100%, ${Math.round((520 * cols * 16) / (rows * 9))}px)` }}
      data-testid="wall-grid"
    >
      {preview && (isVideo ? (
        <video src={preview.preview_url} className="absolute inset-0 h-full w-full object-cover" muted autoPlay loop playsInline />
      ) : (
        <img src={preview.preview_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ))}
      <div
        className="absolute inset-0 grid"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}
      >
        {Array.from({ length: rows }).flatMap((_, r) =>
          Array.from({ length: cols }).map((__, c) => (
            <Cell
              key={cellKey(c, r)} c={c} r={r} tv={byCell[cellKey(c, r)]} selectedTv={selectedTv}
              onDropTv={onDropTv} onPlace={onPlace} onRemove={onRemove} onDragTv={onDragTv}
            />
          ))
        )}
      </div>
    </div>
  );
}

export function TvTray({ tvs, selectedTv, onSelect, onDropBack }) {
  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); onDropBack(e.dataTransfer.getData("text/tv-id")); }}
      className="rounded-xl border border-dashed border-slate-300 bg-white p-4 min-h-[72px]"
      data-testid="wall-tv-tray"
    >
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">TVs disponibles · arrastra o toca y luego toca una celda</div>
      <div className="flex flex-wrap gap-2">
        {tvs.length === 0 && <span className="text-sm text-slate-400">Todas las TVs están colocadas.</span>}
        {tvs.map((tv) => (
          <button
            key={tv.id}
            type="button"
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/tv-id", tv.id)}
            onClick={() => onSelect(selectedTv === tv.id ? null : tv.id)}
            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-200 cursor-grab ${
              selectedTv === tv.id ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-slate-50 text-slate-800 hover:border-blue-400"
            }`}
            data-testid={`wall-tray-tv-${tv.id}`}
          >
            <Tv className="h-3.5 w-3.5" />
            {tv.name}
          </button>
        ))}
      </div>
    </div>
  );
}
